/**
 * The delivery report — above a site's delivery log: how well notifications got out over the last
 * day / week / month. Numbers first (sent, failed, retried, paused, sent to "Everything else"), then
 * per channel (success rate, how long a send takes), the alerts that caused the most notifications
 * (the first thing to tune), and the most common failure reasons.
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Callout } from '../common/Callout';
import { alertActionsErrorText, type DeliveryStats } from './alertActionsModel';
import { BTN_ICON } from '../common/ui';

export interface DeliveryReportProps {
  load: (days: number) => Promise<DeliveryStats>;
}

const RANGES = [{ days: 1, label: 'Today' }, { days: 7, label: '7 days' }, { days: 30, label: '30 days' }];
const ms = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v < 1000 ? `${v} ms` : `${(v / 1000).toFixed(1)} s`);
const channelName = (c: string) => (c === 'email' ? 'Email' : c === 'message_api' ? 'Message API' : c === 'dashboard' ? 'Pop-up on screen' : c);

function Tile({ label, value, hint, tone }: { label: string; value: string | number; hint?: string; tone?: 'danger' | 'muted' }) {
  return (
    <div className="min-w-[8rem] flex-1 rounded-xl border border-border bg-card px-3 py-2.5">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-[22px] font-semibold leading-tight ${tone === 'danger' ? 'text-destructive' : 'text-foreground'}`}>{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function DeliveryReport({ load }: DeliveryReportProps) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<DeliveryStats | null>(null);
  const [error, setError] = useState('');

  const read = useCallback(async () => {
    setError('');
    try {
      setData(await load(days));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not read the delivery report'));
    }
  }, [load, days]);
  useEffect(() => { setData(null); read(); }, [read]);

  const t = data?.totals;
  const delivered = t ? t.sent + t.partial : 0;
  const tried = t ? delivered + t.failed : 0;

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-0 flex-1 text-[15px] font-semibold text-foreground">How notifications got out</h3>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r.days} type="button" onClick={() => setDays(r.days)} aria-pressed={days === r.days}
              className={`rounded-lg border px-2.5 py-1 text-[12px] ${days === r.days ? 'border-brand bg-brand-light font-medium text-brand' : 'border-border text-foreground hover:bg-muted'}`}>
              {r.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={read} className={BTN_ICON} title="Reload" aria-label="Reload">
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
      {!data && !error && <div className="h-20 animate-pulse rounded-lg bg-muted" />}
      {t && (
        <>
          <div className="flex flex-wrap gap-2">
            <Tile label="Sent" value={delivered} hint={tried ? `${Math.round((delivered / tried) * 100)}% of those tried` : undefined} />
            <Tile label="Failed" value={t.failed} tone={t.failed ? 'danger' : undefined} hint={t.queued ? `${t.queued} being retried now` : undefined} />
            <Tile label="Paused" value={t.silenced} hint="held back by a pause" />
            <Tile label="To Everything else" value={t.rerouted} hint="nobody on the matching card could be reached" />
            <Tile label="Nobody to tell" value={t.skipped} />
            <Tile label="Send time" value={ms(t.ms_avg)} hint={t.ms_p90 !== null ? `9 in 10 within ${ms(t.ms_p90)}` : undefined} />
          </div>

          {data.channels.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="py-1.5 pr-3 font-semibold">Channel</th><th className="py-1.5 pr-3 font-semibold">Messages sent</th>
                    <th className="py-1.5 pr-3 font-semibold">Failed</th><th className="py-1.5 pr-3 font-semibold">Got through</th>
                    <th className="py-1.5 pr-3 font-semibold">Average send</th><th className="py-1.5 font-semibold">9 in 10 within</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.channels.map((c) => (
                    <tr key={c.channel} className="text-foreground">
                      <td className="py-1.5 pr-3">{channelName(c.channel)}</td>
                      <td className="py-1.5 pr-3">{c.sent}</td>
                      <td className={`py-1.5 pr-3 ${c.failed ? 'text-destructive' : ''}`}>{c.failed}</td>
                      <td className="py-1.5 pr-3">{c.success_rate === null ? '—' : `${c.success_rate}%`}</td>
                      <td className="py-1.5 pr-3">{ms(c.ms_avg)}</td>
                      <td className="py-1.5">{ms(c.ms_p90)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <h4 className="mb-1 text-[13px] font-semibold text-foreground">Alerts that sent the most</h4>
              {data.noisiest.length === 0 ? <p className="text-[12px] text-muted-foreground">Nothing sent in this time.</p> : (
                <ol className="space-y-1">
                  {data.noisiest.map((r) => (
                    <li key={`${r.kind}:${r.title}`} className="flex items-baseline gap-2 text-[13px] text-foreground">
                      <span className="w-8 shrink-0 text-right font-semibold">{r.total}</span>
                      <span className="min-w-0 flex-1 truncate" title={r.title}>{r.title}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {[r.escalations && `${r.escalations} escalations`, r.failed && `${r.failed} failed`].filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {data.noisiest.length > 0 && <p className="mt-1 text-[11px] text-muted-foreground">Too many? Raise the rule's “Don't repeat it for” time, or narrow its cameras.</p>}
            </div>
            <div>
              <h4 className="mb-1 text-[13px] font-semibold text-foreground">Why sends failed</h4>
              {data.failures.length === 0 ? <p className="text-[12px] text-muted-foreground">No failures.</p> : (
                <ul className="space-y-1">
                  {data.failures.map((f) => (
                    <li key={`${f.channel}:${f.error}`} className="flex items-baseline gap-2 text-[13px] text-foreground">
                      <span className="w-8 shrink-0 text-right font-semibold">{f.count}</span>
                      <span className="min-w-0 flex-1 break-words"><span className="text-muted-foreground">{f.channel ? `${channelName(f.channel)}: ` : ''}</span>{f.error}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
